// Coordinates client-side caching: a global "version" that forces mounted
// useApiData hooks to refetch, a manual refresh(), and prefetchAll() which
// warms the cache right after login so first navigation feels instant.
import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { dataCache, cacheKey, markServerBypass } from '../lib/dataCache';
import { dataDayKey } from '../lib/dataDay';

const DataRefreshContext = createContext(null);

// Max background prefetch requests running at once. Kept low so predictive
// prefetch never floods the browser's connection pool or starves the request
// the user is actually waiting on.
const PREFETCH_CONCURRENCY = 2;

// Endpoints + the exact default params each page first requests, so prefetched
// entries land under the same cache key the page will look up.
const PREFETCH = [
  { path: '/api/data/options', params: undefined },   // dropdowns first
  { path: '/api/data/dashboard', params: undefined },
  { path: '/api/data/products', params: { productFamily: '', granularity: 'month' } },
  { path: '/api/data/resellers', params: { reseller: '', granularity: 'month', from: '', to: '' } },
  { path: '/api/data/reports', params: { reseller: '', productFamily: '', granularity: 'month', from: '', to: '' } },
];

export function DataRefreshProvider({ children }) {
  const [version, setVersion] = useState(0);

  // Background prefetch queue with limited concurrency (see PREFETCH_CONCURRENCY).
  const queueRef = useRef([]);
  const activeRef = useRef(0);

  const pump = useCallback(() => {
    while (activeRef.current < PREFETCH_CONCURRENCY && queueRef.current.length) {
      const job = queueRef.current.shift();
      activeRef.current += 1;
      job().finally(() => { activeRef.current -= 1; pump(); });
    }
  }, []);

  // Queue a (path, params) to warm into the cache. Skips anything already cached
  // or already queued, so calling it repeatedly is cheap.
  const prefetch = useCallback((path, params) => {
    const key = cacheKey(path, params);
    if (dataCache.get(key) !== undefined) return;
    if (queueRef.current.some((j) => j.key === key)) return;
    const job = () => api.get(path, params)
      .then((data) => dataCache.set(key, data))
      .catch(() => {});
    job.key = key;
    queueRef.current.push(job);
    pump();
  }, [pump]);

  // Clear caches and force every mounted useApiData to refetch fresh data.
  const refresh = useCallback(() => {
    markServerBypass();       // next fetches also skip the server cache
    queueRef.current = [];    // drop any pending prefetch work
    dataCache.clearAll();
    setVersion((v) => v + 1);
  }, []);

  // Warm the default view of every page right after login.
  const prefetchAll = useCallback(() => {
    PREFETCH.forEach(({ path, params }) => prefetch(path, params));
  }, [prefetch]);

  // Daily auto-refresh: when the "data day" rolls over (past ~2 AM IST, when the
  // new day's data has landed), pull fresh data even for a tab left open overnight.
  // This is a cheap date-string compare — it only refetches on the actual rollover,
  // so it is NOT query-polling. Backstopped by tab focus / visibility so a laptop
  // reopened in the morning updates immediately.
  const lastDayRef = useRef(dataDayKey());
  useEffect(() => {
    const maybeDailyRefresh = () => {
      const today = dataDayKey();
      if (today !== lastDayRef.current) {
        lastDayRef.current = today;
        refresh();
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') maybeDailyRefresh(); };
    window.addEventListener('focus', maybeDailyRefresh);
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(maybeDailyRefresh, 60 * 1000);
    return () => {
      window.removeEventListener('focus', maybeDailyRefresh);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(id);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ version, refresh, prefetchAll, prefetch }),
    [version, refresh, prefetchAll, prefetch],
  );
  return <DataRefreshContext.Provider value={value}>{children}</DataRefreshContext.Provider>;
}

export function useDataRefresh() {
  return useContext(DataRefreshContext)
    || { version: 0, refresh: () => {}, prefetchAll: () => {}, prefetch: () => {} };
}
